import { usePersistedDataStore } from '../components/store';
import dayjs from 'dayjs';
import { getIFMVUtil } from './getAVUtil';

// Exponential decay / composite scoring
const EXPONENTIAL_DECAY_P = 0.93;
const TOP_K_THRESHOLD = 10;
const USE_GRADED_BONUS = true;
const WEIGHT_RANK = 0.8;
const WEIGHT_CLEAN_HEAD = 0.2;
const SUPPORT_SATURATION_THRESHOLD = 0.05; // 5% below IFMV = full support weight

const getSupportWeight = (adjPrice, ifmv) => {
  if (adjPrice >= ifmv) return 0;
  const deltaRatio = (ifmv - adjPrice) / ifmv;
  const capped = Math.min(deltaRatio / SUPPORT_SATURATION_THRESHOLD, 1);
  return 1 - Math.exp(-4 * capped);
};

/**
 * Graded composite case strength (rank-weighted support + clean-head bonus).
 */
const calculateCompositeCaseStrength = (ifmv, regularComps, reportType = 'standard') => {
  const items = [];
  const priceField = reportType === 'assessment' ? 'adj_IFMV' : 'adj_price';
  const priceArray = regularComps?.[priceField];

  if (priceArray && Array.isArray(priceArray)) {
    priceArray.forEach((adjPrice, index) => {
      if (!adjPrice || isNaN(adjPrice) || adjPrice <= 0) return;
      const supportFactor = getSupportWeight(adjPrice, ifmv);
      items.push({ index, label: supportFactor > 0 ? supportFactor : -1 });
    });
  }

  const weight = (r) => (1 - EXPONENTIAL_DECAY_P) * Math.pow(EXPONENTIAL_DECAY_P, r - 1);

  let wPos = 0;
  let wDen = 0;
  for (const { index, label } of items) {
    const w = weight(index + 1);
    if (label > 0) wPos += w * label;
    wDen += w;
  }
  const ws = wDen > 0 ? wPos / wDen : 0;

  const head = items.slice(0, TOP_K_THRESHOLD);
  const negs = head.reduce((acc, it) => acc + (it.label === -1 ? 1 : 0), 0);
  const hb = !USE_GRADED_BONUS
    ? (negs === 0 ? 1 : 0)
    : (() => {
        const purity = 1 - negs / Math.max(1, TOP_K_THRESHOLD);
        return purity * purity;
      })();

  const finalScore = Math.min(1, Math.max(0, WEIGHT_RANK * ws + WEIGHT_CLEAN_HEAD * hb));
  return Math.round(finalScore * 100);
};

/**
 * Subject-sale override on top of the composite score.
 * - Within date range + below IFMV → 100%
 * - Within date range + at/above IFMV → 0%
 * - Before date range but within 2 years + below IFMV → decaying bonus
 */
const applySubjectSaleWeighting = (baseScore, ifmv, regularComps, earliestCutoffDate) => {
  const pid0 = regularComps?.parcel_id?.[0];
  const pid1 = regularComps?.parcel_id?.[1];
  const isSubjectSale = pid0 != null && pid1 != null && String(pid0) === String(pid1);

  if (!isSubjectSale) return Math.round(baseScore);

  const subjectSalePrice = regularComps?.SalePrice?.[1];
  const subjectTimeAdjSalePrice = regularComps?.TimeAdjSalePrice?.[1];
  const subjectSaleDate = regularComps?.SaleDate?.[1];
  if (!subjectSaleDate) return Math.round(baseScore);

  const daysOutsideEvalDateRange = dayjs(earliestCutoffDate).diff(dayjs(subjectSaleDate), 'days');
  const withinEvalDateRange = daysOutsideEvalDateRange <= 0;
  const withinTwoYears = daysOutsideEvalDateRange <= 730;
  const effectiveSalePrice = withinEvalDateRange
    ? subjectSalePrice
    : (subjectTimeAdjSalePrice || subjectSalePrice);
  const soldBelowIFMV = effectiveSalePrice < ifmv;

  if (soldBelowIFMV) {
    if (withinEvalDateRange) return 100;
    if (withinTwoYears) {
      const timePenaltyFactor = daysOutsideEvalDateRange > 0
        ? Math.max(0, 1 - daysOutsideEvalDateRange / 730)
        : 1;
      return Math.round(Math.min(100, baseScore * (1 + 0.5 * timePenaltyFactor)));
    }
  } else if (withinEvalDateRange) {
    return 0;
  }

  return Math.round(baseScore);
};

/**
 * Display / save case strength for a case index in compSheet.
 * Returns { caseStrengthA } for existing call sites.
 */
export const getCaseStrengthPercentage = (comp, reportType = 'standard') => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const compSheet = usePersistedDataStore.getState().compSheet;
  const regularComps = compSheet?.[comp];
  const ifmv = getIFMVUtil(regularComps, 0);

  if (!ifmv || ifmv === 0 || isNaN(ifmv) || !regularComps) {
    return { caseStrengthA: 0 };
  }

  const baseScore = calculateCompositeCaseStrength(ifmv, regularComps, reportType);
  const finalScore = applySubjectSaleWeighting(
    baseScore,
    ifmv,
    regularComps,
    negotiationObj?.earliestCutoffDate
  );

  return { caseStrengthA: finalScore };
};

export const getCaseStrengthColor = (percentage) => {
  return percentage >= 50 ? '#16a34a' : '#dc2626';
};
